'use client';

import React, { useEffect } from 'react';
import {
  applyFreestarPageTargeting,
  ensureFreestarConfig,
  isAdLightDomHintActive,
  isArticlePath,
  neutralizeFreestarStickyRailForReactSlots,
  passFreestarIdentity,
  resolveFreestarChannel,
  type FreestarChannel,
} from '@benzinga/ads-utils';
import { useBenzingaEdge } from '@benzinga/edge';
import { useUser } from '@benzinga/user-context';
import { loadFreestarAdSlot } from './loadFreestarAdSlot';
import { useFreestarPathname } from './useFreestarPathname';

const DISABLED_SCRIPT_PATHS = [
  '/welcome',
  '/privacy-policy',
  '/terms-and-conditions',
  '/disclaimer',
  '/affiliate',
  '/sponsored',
  '/login',
  '/services',
  '/free-newsletters',
  '/about',
  '/contact',
];

const isAdsDisabledPath = (pathname: string) =>
  DISABLED_SCRIPT_PATHS.some(path => pathname === path || pathname.startsWith(`${path}/`));

export const FreestarBootstrap: React.FC<{
  channel?: FreestarChannel;
  disableFooterDisplayAd?: boolean;
  pathname?: string;
  targeting?: Record<string, string | string[]>;
}> = ({ channel: channelOverride, disableFooterDisplayAd, pathname: pathnameOverride, targeting }) => {
  const pathname = useFreestarPathname(pathnameOverride);
  const channel = channelOverride ?? resolveFreestarChannel(pathname);
  const adsDisabledPath = isAdsDisabledPath(pathname);
  const { adLightEnabled, disabled } = useBenzingaEdge();
  const user = useUser();
  const adsFullyDisabled = adsDisabledPath;
  const suppressNonSidebarAds = adLightEnabled || disabled.displayAd || isAdLightDomHintActive();
  const stickyFooterDisabled =
    adsFullyDisabled || suppressNonSidebarAds || !!disableFooterDisplayAd || isArticlePath(pathname);
  const [, setStickyFooterViewport] = React.useState(() => (typeof window === 'undefined' ? 1024 : window.innerWidth));

  ensureFreestarConfig({
    channel,
    disabledProducts: {
      googleInterstitial: adsFullyDisabled || suppressNonSidebarAds,
      stickyFooter: stickyFooterDisabled,
      stickyRail: true,
    },
  });

  if (!adsFullyDisabled) {
    neutralizeFreestarStickyRailForReactSlots();
  }

  useEffect(() => {
    if (adsFullyDisabled) return;
    void loadFreestarAdSlot();
  }, [adsFullyDisabled]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const syncViewport = () => setStickyFooterViewport(window.innerWidth);
    media.addEventListener('change', syncViewport);
    return () => media.removeEventListener('change', syncViewport);
  }, []);

  useEffect(() => {
    ensureFreestarConfig({
      channel,
      disabledProducts: {
        googleInterstitial: adsFullyDisabled || suppressNonSidebarAds,
        stickyFooter: stickyFooterDisabled,
        stickyRail: true,
      },
    });
    if (!adsFullyDisabled) {
      neutralizeFreestarStickyRailForReactSlots();
    }

    if (adsFullyDisabled) return;

    let cancelled = false;

    const syncTargetingPageviewAndIdentity = async () => {
      await applyFreestarPageTargeting(targeting, {
        holdAuctions: true,
        trackPageview: !cancelled,
      });
      if (cancelled) return;
      passFreestarIdentity(user?.email);
    };

    void syncTargetingPageviewAndIdentity();

    return () => {
      cancelled = true;
    };
  }, [adsFullyDisabled, channel, pathname, stickyFooterDisabled, suppressNonSidebarAds, targeting, user?.email]);

  useEffect(() => {
    if (user?.email) return;
    passFreestarIdentity(null);
  }, [user?.email]);

  return null;
};
